/**
 * RenderCullingModule — the RenderSub LOD-selection + culling pass (Graphics G2).
 *
 * EngineX's RenderSub phase is where LOD + culling belong (so the Render/WebGPU phase
 * only submits visible, appropriately-detailed geometry). Each frame this system:
 *   • selects each registered THREE.LOD's level by camera distance (`lod.update`);
 *   • distance-culls registered objects beyond a far cutoff (`object.visible`);
 *   • runs per-instance cullers for instanced crowds (count-packing — G2 P2).
 * Per-OBJECT frustum culling is three's own automatic pass (culled objects emit no
 * draw call); this system adds what three doesn't do.
 *
 * Reuses the engine's `extractFrustumPlanes` (passed to crowd cullers).
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { extractFrustumPlanes } from 'enginex/render/culling/frustumCull.compute.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { activePreset } from '../lib/quality_tiers.js';

export type CrowdCuller = (camPos: THREE.Vector3, frustumPlanes: Float32Array) => void;

export class RenderCullingModule implements IModule {
  readonly name = 'RenderCulling';
  private app!: App;
  private readonly lods = new Set<THREE.LOD>();
  private readonly cullables = new Set<THREE.Object3D>();
  private readonly crowdCullers = new Set<CrowdCuller>();
  private readonly camPos = new THREE.Vector3();
  private readonly projView = new THREE.Matrix4();
  private readonly sceneShift = new THREE.Matrix4();
  /** Test hook: the LOD level chosen for each registered LOD this frame. */
  lastUpdateRan = false;

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('RenderCullLOD', Phase.RenderSub, 10, () => this.update()));
  }

  registerLOD(lod: THREE.LOD): void { this.lods.add(lod); this.cullables.add(lod); }
  unregisterLOD(lod: THREE.LOD): void { this.lods.delete(lod); this.cullables.delete(lod); }
  registerCullable(o: THREE.Object3D): void { this.cullables.add(o); }
  unregisterCullable(o: THREE.Object3D): void { this.cullables.delete(o); }
  registerCrowdCuller(fn: CrowdCuller): void { this.crowdCullers.add(fn); }
  unregisterCrowdCuller(fn: CrowdCuller): void { this.crowdCullers.delete(fn); }

  /** Drive one cull/LOD pass against a specific camera (used by the system + tests). */
  runWith(cam: THREE.Camera): void {
    cam.getWorldPosition(this.camPos);
    cam.updateMatrixWorld();
    // Cull in SCENE-LOCAL space. Under the FP64 origin rebase the scene root is
    // shifted so the camera renders near (0,0,0), while scene children (crowd
    // instance matrices, cullable/LOD positions) keep ABSOLUTE world coords.
    // Testing absolute positions against render-space planes/camPos culled
    // every crowd instance and cullable beyond the first rebase anchor — the
    // invisible-versonas bug. The rebase is a pure translation, so folding
    // scene.position into the plane matrix + camPos restores one shared space.
    const scenePos = this.app.world.scene.position;
    this.camPos.sub(scenePos);
    this.projView.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
    this.projView.multiply(this.sceneShift.makeTranslation(scenePos.x, scenePos.y, scenePos.z));
    const planes = extractFrustumPlanes(this.projView);
    // G5: the active quality tier sets the far-cull distance live — a lower tier draws less of the
    // world (shorter cutoff), trading view distance for draw-call + fill savings on weak GPUs.
    const farCull = activePreset().farCull;
    for (const lod of this.lods) lod.update(cam);
    // THE OWNER-HIDDEN LAW (2026-09-23): a cullable's owner may hide it — a deed's exterior while the player
    // stands in its interior — and this per-frame write used to turn it straight back on (the exterior clipped
    // through every LOD-path deed's interior). `userData.ownerHidden` keeps it off until the owner restores it.
    for (const obj of this.cullables) obj.visible = !obj.userData.ownerHidden && this.camPos.distanceTo(obj.position) <= farCull;
    for (const cull of this.crowdCullers) cull(this.camPos, planes);
    this.lastUpdateRan = true;
  }

  private update(): void {
    const cam = this.camera();
    if (cam) this.runWith(cam);
  }

  private camera(): THREE.Camera | null {
    try { return this.app.renderer.cameras.main; } catch { return null; }
  }
}
